/**
 * The two modes a cycle can be in, as far as rank is concerned.
 *
 *   ranking  The cycle is still being prioritized. Items are being ranked — by
 *            sessions, by tracks — and nothing is official yet.
 *   ranked   A Plan of Record is authorized and the cycle is in execution.
 *            Each item it holds has one official rank, and that rank is fixed
 *            until a new Plan of Record replaces it.
 *
 * Owner ruling, 2026-10-13: these are different modes and a page should look
 * different in each. The mode belongs to the cycle, not to an item — the same
 * item can be fixed at #4 in this year's plan while being ranked again for
 * next year's.
 *
 * The test for `ranked`: the cycle has an active Plan of Record snapshot
 * (`plan_of_record_snapshots.superseded_by_snapshot_id IS NULL`) whose plan a
 * later plan has not replaced (migration 919, plan_force_states).
 */

import { getTypedSupabaseClient } from './typedSupabaseClient';
import { fetchPlanStatesByCycle } from './planForceService';

export type RankMode =
  | { mode: 'ranking' }
  | {
      mode: 'ranked';
      planCycleId: string;
      planName: string;
      authorizedAt: string;
      /** Display name, or a user id on authorizations made before names were recorded. */
      authorizedBy: string | null;
    };

export interface ActivePlanOfRecordRow {
  plan_cycle_id: string;
  authorized_at: string;
  authorized_by: string | null;
  plan_cycle_name: string | null;
}

/** Pure: the newest active Plan of Record decides; none means still ranking. */
export function resolveRankMode(active: readonly ActivePlanOfRecordRow[]): RankMode {
  if (active.length === 0) return { mode: 'ranking' };
  const newest = [...active].sort((a, b) => b.authorized_at.localeCompare(a.authorized_at))[0];
  return {
    mode: 'ranked',
    planCycleId: newest.plan_cycle_id,
    planName: newest.plan_cycle_name?.trim() || 'Plan of Record',
    authorizedAt: newest.authorized_at,
    authorizedBy: newest.authorized_by,
  };
}

async function readActivePlansOfRecord(planCycleIds: string[]): Promise<ActivePlanOfRecordRow[] | null> {
  if (planCycleIds.length === 0) return [];
  const { data, error } = await getTypedSupabaseClient()
    .from('plan_of_record_snapshots')
    .select('plan_cycle_id, authorized_at, authorized_by, plan_cycles(name)')
    .in('plan_cycle_id', planCycleIds)
    .is('superseded_by_snapshot_id', null);
  if (error) return null;
  // A plan a later plan has replaced (migration 919) no longer holds ranks of record.
  // If the plan states cannot be read, the active snapshot alone decides, as before.
  const states = await fetchPlanStatesByCycle((data ?? []).map((r) => r.plan_cycle_id));
  return (data ?? [])
    .filter((r) => states?.get(r.plan_cycle_id)?.state !== 'replaced')
    .map((r) => ({
      plan_cycle_id: r.plan_cycle_id,
      authorized_at: r.authorized_at,
      authorized_by: r.authorized_by,
      plan_cycle_name: r.plan_cycles?.name ?? null,
    }));
}

/** The mode of a plan cycle. Null when it could not be read — say nothing rather than guess. */
export async function fetchPlanCycleRankMode(planCycleId: string): Promise<RankMode | null> {
  const active = await readActivePlansOfRecord([planCycleId]);
  return active === null ? null : resolveRankMode(active);
}

/**
 * The mode of a prioritization cycle. A cycle reaches a Plan of Record through
 * its priority list or its item pool serving as a plan cycle's input, the same
 * join the database uses (`plan_cycle_governing_cycle`, migration 843). Null
 * when it could not be read.
 */
export async function fetchCycleRankMode(cycleId: string): Promise<RankMode | null> {
  const db = getTypedSupabaseClient();
  const [{ data: sets, error: setsError }, { data: pools, error: poolsError }] = await Promise.all([
    db.from('priority_sets').select('id').eq('cycle_id', cycleId),
    db.from('item_pools').select('id').eq('cycle_id', cycleId),
  ]);
  if (setsError || poolsError) return null;
  const inputIds = [...(sets ?? []), ...(pools ?? [])].map((r) => r.id);
  if (inputIds.length === 0) return { mode: 'ranking' };

  const { data: plans, error: plansError } = await db
    .from('plan_cycles')
    .select('id')
    .in('input_type', ['published_priority_list', 'item_pool'])
    .in('input_reference_id', inputIds);
  if (plansError) return null;

  const active = await readActivePlansOfRecord((plans ?? []).map((p) => p.id));
  return active === null ? null : resolveRankMode(active);
}
